import { keymap } from 'prosemirror-keymap';
import {
  chainCommands,
  liftEmptyBlock,
  newlineInCode,
  toggleMark,
} from 'prosemirror-commands';
import { undo, redo } from 'prosemirror-history';
import { splitListItem } from 'prosemirror-schema-list';

import { findMarkRange, isMarkActive } from './commands';

/**
 * Insere a quebra de linha.
 *
 * Existe para o `Shift-Enter` nunca chegar ao comando do
 * `@chatwoot/prosemirror-schema`, que faz
 * `tr.insertText(' ').replaceSelectionWith(br)` — um espaco LITERAL antes da
 * quebra. Apagar a quebra depois deixava o espaco orfao no fim da linha, e
 * como o editor usa `white-space: pre-wrap` ele aparecia como um recuo do
 * nada. Era o "espaco quase como um tab" ao voltar para a linha de cima.
 *
 * O espaco tambem entrava no que era gravado, entao nao era so aparencia.
 *
 * Ultimo da cadeia: os comandos antes dele tratam bloco de codigo, codigo
 * inline e lista, e so o que sobrar chega aqui.
 *
 * @param {EditorState} state
 * @param {Function} dispatch
 * @returns {boolean}
 */
export function insertHardBreak(state, dispatch) {
  const br = state.schema.nodes.hard_break;

  if (!br) return false;

  if (dispatch) {
    dispatch(state.tr.replaceSelectionWith(br.create()).scrollIntoView());
  }

  return true;
}

/**
 * Quebra o codigo inline e revela as crases.
 *
 * Codigo inline nao atravessa quebra de linha: `a\nb` nao e codigo em lugar
 * nenhum. Antes o mark seguia valendo dos dois lados da quebra, e o editor
 * mostrava como codigo algo que chegaria ao contato com as crases a mostra.
 *
 * Agora a marcacao e desfeita e as crases viram texto de verdade nas pontas —
 * o editor passa a exibir exatamente o que sera enviado.
 *
 * @param {EditorState} state
 * @param {Function} dispatch
 * @returns {boolean} true quando tratou a tecla
 */
export function breakInlineCode(state, dispatch) {
  const codeMark = state.schema.marks.code;
  const br = state.schema.nodes.hard_break;

  if (!codeMark || !br) return false;
  // Dentro de um bloco de codigo a quebra e interna; quem trata e newlineInCode.
  if (state.selection.$from.parent.type.spec.code) return false;
  if (!isMarkActive(state, codeMark)) return false;

  const range = findMarkRange(state.selection.$from, codeMark);
  if (!range) return false;

  // Nas bordas do trecho a quebra cai FORA do codigo: nao ha o que partir, e
  // desfazer a marcacao ali joga a crase para o lado errado da quebra — com o
  // cursor no fim de `\`teste\`` saia "\`teste  \n\`". Devolver false deixa o
  // keymap base inserir o `hard_break` normalmente.
  if (state.selection.from <= range.from || state.selection.to >= range.to) {
    return false;
  }

  if (dispatch) {
    const tr = state.tr;

    tr.removeMark(range.from, range.to, codeMark);

    // O fim primeiro: inserir no inicio deslocaria a posicao do fim.
    tr.insertText('`', range.to);
    tr.insertText('`', range.from);

    // A crase de abertura empurrou o cursor uma posicao para a frente.
    tr.replaceRangeWith(
      state.selection.from + 1,
      state.selection.to + 1,
      br.create()
    );

    dispatch(tr.scrollIntoView());
  }

  return true;
}

export function formattingKeymap(schema) {
  const keys = {};

  const bindBoth = (key, command) => {
    keys[`Mod-${key.toLowerCase()}`] = command;
    keys[`Mod-${key.toUpperCase()}`] = command;
  };

  if (schema.marks.strong) {
    bindBoth('b', toggleMark(schema.marks.strong));
  }

  if (schema.marks.em) {
    bindBoth('i', toggleMark(schema.marks.em));
  }

  if (schema.marks.strike) {
    keys['Mod-Shift-x'] = toggleMark(schema.marks.strike);
    keys['Mod-Shift-X'] = toggleMark(schema.marks.strike);
  }

  if (schema.marks.code) {
    bindBoth('e', toggleMark(schema.marks.code));
  }

  // Antes do keymap do pacote, que liga Shift-Enter a `chainCommands(exitCode,
  // ...)`. O `exitCode` sai do bloco de codigo em vez de quebrar a linha
  // dentro dele — era o "fui jogado para fora das crases".
  //
  // Dentro de uma lista, Shift-Enter abre o proximo item, igual ao Enter. A
  // quebra solta deixava a linha nova sem marcador, e nao ha lista de verdade
  // do outro lado: no WhatsApp cada item e uma linha comecada por `-`, entao
  // uma linha sem marcador dentro do item chega ao contato como texto avulso.
  //
  // Vale mais ainda na caixa de resposta: com "Enter envia" ligado, o Enter
  // manda a mensagem e Shift-Enter e a UNICA forma de criar linha ali. Sem
  // isto, montar uma lista na conversa era impossivel.
  //
  // `splitListItem` devolve false fora de lista, e a quebra fica com o
  // `insertHardBreak` daqui — que NAO pode ser o comando do pacote, porque
  // aquele insere um espaco junto.
  const splitItem = schema.nodes.list_item
    ? splitListItem(schema.nodes.list_item)
    : null;

  const quebrarLinha = chainCommands(
    newlineInCode,
    breakInlineCode,
    ...(splitItem ? [splitItem] : []),
    // Antes do `insertHardBreak`: num item de lista VAZIO o `splitListItem`
    // devolve false, e e o `liftEmptyBlock` que tira o cursor da lista. Sem
    // ele o Enter ali passaria a empilhar quebras dentro do item, e nao haveria
    // como sair da lista pelo teclado.
    liftEmptyBlock,
    insertHardBreak
  );

  keys['Shift-Enter'] = quebrarLinha;

  /**
   * Como `insertHardBreak`, mas so no nivel de topo.
   *
   * Fora dele o Enter continua sendo o do `baseKeyMaps`, com tudo que ele ja
   * fazia: abre o proximo item da lista, sai da lista no item vazio, parte o
   * paragrafo dentro da citacao e, no paragrafo vazio, tira o cursor de la.
   *
   * Devolver true sempre — que era o efeito da quebra — deixava o `baseKeyMaps`
   * inalcancavel, e nao havia como sair da citacao pelo teclado: quem digitava
   * `> ` ficava preso. O Shift-Enter segue quebrando ali, que e o jeito de
   * fazer uma linha nova DENTRO da citacao.
   *
   * @param {EditorState} state
   * @param {Function} dispatch
   * @returns {boolean}
   */
  const quebrarLinhaDeTexto = (state, dispatch) => {
    if (state.selection.$from.depth > 1) return false;

    return insertHardBreak(state, dispatch);
  };

  // O Enter quebra a linha no nivel de topo, e nao cria paragrafo.
  //
  // No modelo de linhas a fronteira de paragrafo JA vale uma linha em branco,
  // entao um Enter que criasse paragrafo somaria duas de uma vez no meio de um
  // texto — e uma so no fim, onde nao ha nada depois. Com quebra, cada Enter
  // vale uma linha em qualquer posicao, que e como o compositor do WhatsApp se
  // comporta: aperta-se Enter duas vezes para abrir uma linha em branco.
  //
  // Isto NAO alcanca a caixa de resposta com "Enter envia" ligado: la o
  // `handleDOMEvents.keydown` do componente ja chamou `preventDefault`, e o
  // ProseMirror para antes de chegar em qualquer keymap.
  keys.Enter = chainCommands(
    newlineInCode,
    breakInlineCode,
    quebrarLinhaDeTexto
  );

  keys['Mod-z'] = undo;
  keys['Shift-Mod-z'] = redo;
  keys['Mod-y'] = redo;

  return keymap(keys);
}

export default formattingKeymap;
